import Button from "components/common/Button";
import React, { useEffect, useMemo, useState } from "react";
import SpaceTaskAddModal from "./SpaceTaskAddModal";
import { debounce } from "lodash";
import { useParams, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

import WorkforceAnalyticsCard from "components/common/WorkforceAnalyticsCard";
import { formatMoney } from "helpers";
import SynthTooltip from "components/common/SynthTooltip";
import Dropdown, { DropdownProps } from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import useToggle from "hooks/useToggle";
import {
    Space,
    SpaceTaskFilters,
    SpaceTaskPriorityEnum,
    SpaceTaskStatusEnum,
    TaskScheduleTypeEnum,
} from "redux/Ldb/types/spaceTypes";
import { useGetSpaceTasks } from "hooks/spaceHooks";
import LoadingContent from "components/common/LoadingContent";
import VendorAvatar from "components/common/VendorAvatar";
import { normalizeMembers } from "utils/spaceUtils";
import SpaceTaskOneTimeList from "./SpaceTaskOneTimeList";
import SpaceTaskRecurringList from "./SpaceTaskRecurringList";
import SpaceMessageLoadingState from "../SpaceMessage/SpaceMessageLoadingState";
import SpaceEmptyContent from "../SpaceEmptyContent";
import TaskDetails from "./SpaceTaskDetails";
import { capitalizeFirstLetter } from "helpers/transformStrings";

type SpaceDetailsTaskTabProps = {
    spaceData: Space;
};

const SpaceDetailsTaskTab = (props: SpaceDetailsTaskTabProps) => {
    const { spaceData } = props;
    const { spaceId } = useParams();
    const [searchParams] = useSearchParams();
    const taskIdParams = searchParams.get("taskId");
    const [taskId, setTaskId] = useState<string>("");
    const [taskType, setTaskType] = useState<string>("");
    const [addStatus, setAddStatus] = useState<SpaceTaskStatusEnum | string>("");

    const [filters, setFilters] = useState<SpaceTaskFilters>({
        page: 1,
        limit: 100,
        taskType: TaskScheduleTypeEnum.oneTime,
    });
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");

    const [openSpaceTaskAddModal, toggleOpenSpaceTaskAddModal] = useToggle();

    const getSpaceTaskListQuery = useGetSpaceTasks(
        useMemo(
            () => ({
                path: { spaceId: spaceId },
                params: {
                    page: filters.page,
                    limit: filters.limit,
                    search: debouncedSearch,
                    taskType: filters.taskType,
                    priority: filters.priority,
                    status: filters.status,
                    assignedToId: filters.assignedToId,
                },
            }),
            [
                spaceId,
                debouncedSearch,
                filters.taskType,
                filters.priority,
                filters.status,
                filters.assignedToId,
                filters.page,
                filters.limit,
            ]
        )
    );

    const members = normalizeMembers(spaceData);
    const selectedMember = members?.find(
        (member) =>
            (member.ldbId || member.guestId) === filters.assignedToId ||
            (member.isSynth && filters.assignedToId === "synth")
    );

    const contentProps = {
        spaceData: spaceData,
        getSpaceTaskListQuery: getSpaceTaskListQuery,
        filters: filters,
        setFilters: setFilters,
        toggleOpenSpaceTaskAddModal,
        setAddStatus,
        setTaskId,
        setTaskType,
    };

    useEffect(() => {
        if (taskIdParams) {
            setTaskId(taskIdParams);
        } else {
            setTaskId("");
        }
    }, [taskIdParams]);

    const isEmpty =
        getSpaceTaskListQuery?.data?.tasks?.length === 0 &&
        !getSpaceTaskListQuery.isFetching &&
        !getSpaceTaskListQuery.data?.stats?.allTasks;

    return (
        <>
            {!taskId && (
                <LoadingContent
                    loading={getSpaceTaskListQuery.isLoading}
                    error={getSpaceTaskListQuery.isError}
                    renderLoading={() => (
                        <div className="flex h-full py-20 justify-center items-center">
                            <SpaceMessageLoadingState
                                isLoading={true}
                                message="Loading tasks..."
                                className="h-auto"
                            />
                        </div>
                    )}
                    renderError={() => (
                        <div className="flex h-full py-20 justify-center items-center">
                            <SpaceEmptyContent
                                icon="ri-error-warning-line"
                                title="Something went wrong"
                                description="Please try again later."
                                action={() => {
                                    getSpaceTaskListQuery.refetch();
                                }}
                                actionText="Retry"
                            />
                        </div>
                    )}
                >
                    <div>
                        {isEmpty ? (
                            <div className="py-20 flex justify-center items-center h-full">
                                <SpaceEmptyContent
                                    icon="ri-list-check-3"
                                    title="No tasks here yet"
                                    description="You haven’t created any tasks in this space. Use tasks to track planning, procurement, and production activities with your team."
                                    action={() => {
                                        toggleOpenSpaceTaskAddModal();
                                    }}
                                    actionText="Create your first task"
                                />
                            </div>
                        ) : (
                            <div className="font-rocGroteskMedium ">
                                <div className="px-[24px]">
                                    <LoadingContent
                                        loading={getSpaceTaskListQuery.isLoading}
                                        error={getSpaceTaskListQuery.isError}
                                        renderLoading={() => {
                                            return (
                                                <div className="grid grid-cols-3 items-center space-x-3 w-full">
                                                    {Array.from({ length: 3 }).map((_, idx) => (
                                                        <Skeleton
                                                            key={idx}
                                                            width={"100%"}
                                                            height={100}
                                                            highlightColor="#E2E8F0"
                                                            borderRadius={8}
                                                            baseColor="#F1F5F9"
                                                            className="custom-skeleton"
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        }}
                                    >
                                        <div className="grid grid-cols-3 gap-3">
                                            {[
                                                {
                                                    title: "Completed tasks",
                                                    value:
                                                        getSpaceTaskListQuery?.data?.stats
                                                            ?.completedTasks || 0,
                                                },

                                                {
                                                    title: "Overdue tasks",
                                                    value:
                                                        getSpaceTaskListQuery?.data?.stats
                                                            ?.overdueTasks || 0,
                                                },
                                                {
                                                    title: "Ongoing tasks",
                                                    value:
                                                        getSpaceTaskListQuery?.data?.stats
                                                            ?.todoTasks || 0,
                                                },
                                            ]?.map((item, idx) => (
                                                <WorkforceAnalyticsCard
                                                    key={idx}
                                                    title={item.title}
                                                    value={formatMoney().format(item.value)}
                                                    containerClassname="!bg-[#FFFF] rounded-lg !h-auto !border-[#E2E8F0] !shadow-[0px_4px_8px_-4px_rgba(16,24,40,0.03)]"
                                                    titleClassname="text-sm text-g-75"
                                                    valueClassname="text-lg text-g-75"
                                                    tooltip={
                                                        <SynthTooltip
                                                            divId={"completed-tasks"}
                                                            position={"top"}
                                                            icon={
                                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                                            }
                                                            contentIcon={<></>}
                                                            title=""
                                                            content=""
                                                            styling={{
                                                                width: "270px",
                                                                background: "#142837",
                                                                borderRadius: "6px",
                                                            }}
                                                            contentClass="text-xs opacity-50"
                                                        />
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </LoadingContent>

                                    <div className="flex items-center space-x-2 mt-4 mb-6 max-sm:grid max-sm:space-x-0 max-sm:space-y-2 max-sm:grid-cols-1">
                                        <TextInput
                                            name={"search"}
                                            value={filters.search}
                                            type={"text"}
                                            inputPlaceholder={"Search tasks"}
                                            inputClassName={
                                                "!h-[38px] pl-[0px] !bg-white text-sm !mb-0"
                                            }
                                            onChange={(e) => {
                                                setFilters({
                                                    ...filters,
                                                    search: e.target.value,
                                                });
                                            }}
                                            onInput={debounce((e) => {
                                                setDebouncedSearch(e.target.value);
                                            }, 1000)}
                                            containerClassname={"!w-full"}
                                            leftIcon={
                                                <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                                            }
                                            inputContainerClassname={
                                                "!rounded-[4px] max-sm:!w-full !pl-[25px] !h-[40px] !bg-white !border !border-slate-200"
                                            }
                                        />

                                        <DetailDropdown
                                            value={filters?.taskType}
                                            label="Task type"
                                            handleChange={(_, value) => {
                                                setFilters({
                                                    ...filters,
                                                    taskType: value as TaskScheduleTypeEnum,
                                                });
                                            }}
                                            dropdownOptions={Object.values(
                                                TaskScheduleTypeEnum
                                            )?.map((item) => {
                                                return {
                                                    label: capitalizeFirstLetter(
                                                        item?.split("_")?.join(" ")
                                                    ),
                                                    value: item,
                                                };
                                            })}
                                        />

                                        <DetailDropdown
                                            value={filters?.priority}
                                            label="Priority"
                                            handleChange={(_, value) => {
                                                setFilters({
                                                    ...filters,
                                                    priority: value as SpaceTaskPriorityEnum,
                                                });
                                            }}
                                            handleClear={() => {
                                                setFilters({
                                                    ...filters,
                                                    priority: "",
                                                });
                                            }}
                                            dropdownOptions={Object.values(
                                                SpaceTaskPriorityEnum
                                            )?.map((item) => ({
                                                label: capitalizeFirstLetter(
                                                    item?.split("_")?.join(" ")
                                                ),
                                                value: item,
                                            }))}
                                        />

                                        <DetailDropdown
                                            value={filters?.status}
                                            label="Status"
                                            handleChange={(_, value) => {
                                                setFilters({
                                                    ...filters,
                                                    status: value as SpaceTaskStatusEnum,
                                                });
                                            }}
                                            handleClear={() => {
                                                setFilters({
                                                    ...filters,
                                                    status: "",
                                                });
                                            }}
                                            dropdownOptions={Object.values(
                                                SpaceTaskStatusEnum
                                            )?.map((item) => ({
                                                label: capitalizeFirstLetter(
                                                    item?.split("_")?.join(" ")
                                                ),
                                                value: item,
                                            }))}
                                        />

                                        <Dropdown
                                            name="filter"
                                            dropdownTriggerClassName={" "}
                                            handleChange={(_, value) => {
                                                setFilters({
                                                    ...filters,
                                                    assignedToId: value,
                                                });
                                            }}
                                            dropdown={
                                                <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                                                    <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                        {filters?.assignedToId
                                                            ? selectedMember?.fullName
                                                                ? selectedMember?.fullName
                                                                : selectedMember?.email
                                                            : "Assigned to"}
                                                    </span>

                                                    <div className="flex items-center space-x-2">
                                                        {filters?.assignedToId && (
                                                            <i
                                                                onClick={(e) => {
                                                                    e?.stopPropagation();
                                                                    setFilters({
                                                                        ...filters,
                                                                        assignedToId: "",
                                                                    });
                                                                }}
                                                                className="ri-close-circle-fill text-sm text-slate-500"
                                                            ></i>
                                                        )}
                                                        <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                    </div>
                                                </div>
                                            }
                                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                                            dropdownContainerClasses={`shadow-cardShadow-4 !right-0 !mt-0  !w-[159px] max-sm:!w-full  rounded !max-h-fit !p-0 !top-[105%]`}
                                            wholeContainerClass={`max-sm:!w-full`}
                                            dropdownItemsClasses="!bg-transparent"
                                            dropdownOptions={members?.map((member) => ({
                                                label: member?.fullName
                                                    ? member?.fullName
                                                    : member?.email,
                                                value: member?.isSynth
                                                    ? "synth"
                                                    : member?.ldbId || member?.guestId,
                                                icon: (
                                                    <VendorAvatar
                                                        size={30}
                                                        imageSrc={member?.avatar}
                                                        name={
                                                            member?.fullName
                                                                ? member?.fullName
                                                                : member?.email
                                                        }
                                                    />
                                                ),
                                            }))}
                                        />

                                        <div className="!w-fit">
                                            <Button
                                                btnClassname="!py-2.5 !px-4 !w-[130px]"
                                                btnText="Add new task"
                                                onClick={() => {
                                                    toggleOpenSpaceTaskAddModal();
                                                    setAddStatus("");
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {filters.taskType === TaskScheduleTypeEnum.oneTime ? (
                                        <SpaceTaskOneTimeList {...contentProps} />
                                    ) : (
                                        <SpaceTaskRecurringList {...contentProps} />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </LoadingContent>
            )}

            {!taskId && (
                <SpaceTaskAddModal
                    open={openSpaceTaskAddModal}
                    closeModal={toggleOpenSpaceTaskAddModal}
                    spaceData={spaceData}
                    status={addStatus}
                />
            )}
            {taskId && (
                <TaskDetails
                    taskId={taskId}
                    setTaskId={setTaskId}
                    type={taskType}
                    setType={setTaskType}
                    spaceData={spaceData}
                    handleRefetch={getSpaceTaskListQuery.refetch}
                />
            )}
        </>
    );
};

export default SpaceDetailsTaskTab;

function DetailDropdown(props: DropdownProps & { handleClear?: () => void }) {
    const { label, handleClear, ...rest } = props;
    return (
        <Dropdown
            name="filter"
            dropdownTriggerClassName={" "}
            dropdown={
                <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                    <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                        {rest?.value ? rest?.value?.split("_")?.join(" ") : label}
                    </span>

                    <div className="flex items-center space-x-2">
                        {rest?.value && handleClear && (
                            <i
                                onClick={(e) => {
                                    e?.stopPropagation();
                                    handleClear();
                                }}
                                className="ri-close-circle-fill text-sm text-slate-500"
                            ></i>
                        )}
                        <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                    </div>
                </div>
            }
            dropdownClassName={"!w-full !mb-10 !-mt-7"}
            dropdownContainerClasses={`shadow-cardShadow-4 !right-0 !mt-0  !w-[159px] max-sm:!w-full  rounded !max-h-fit !p-0 !top-[105%]`}
            wholeContainerClass={`max-sm:!w-full`}
            dropdownItemsClasses="!bg-transparent"
            {...rest}
        />
    );
}
